// src/utils/studentIdentity.js
//
// Reconnaître la MÊME PERSONNE à travers plusieurs fiches (users/, students/,
// ajouts manuels…). Utilisé par Students.js (regroupement), Users.js
// (détection des doublons) et AttendancePage.js (éviter d'en créer).
//
// Règle : deux fiches sont la même personne si
//   • l'une pointe explicitement vers l'autre (mergedInto, userId, studentId), OU
//   • elles ont le même prénom + nom (accents, majuscules, tirets et ordre
//     prénom/nom ignorés) ET des dates de naissance compatibles (identiques,
//     ou absentes d'un côté). Deux homonymes avec des dates de naissance
//     différentes restent séparés.
// Une fiche marquée separateIdentity: true n'est jamais regroupée par le nom.

import { normalizeText, dayKey } from './billing';

const FR_MONTHS = {
  janvier: 0, fevrier: 1, mars: 2, avril: 3, mai: 4, juin: 5,
  juillet: 6, aout: 7, septembre: 8, octobre: 9, novembre: 10, decembre: 11,
};

/** Date de naissance depuis tous les formats rencontrés dans la base */
export const parseBirthDate = (v) => {
  if (!v) return null;
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
  if (typeof v.toDate === 'function') return v.toDate();
  if (typeof v === 'object' && typeof v.seconds === 'number') return new Date(v.seconds * 1000);
  if (typeof v === 'number') return new Date(v);
  if (typeof v === 'string') {
    const s = v.trim();
    let m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) return new Date(+m[1], +m[2] - 1, +m[3]);
    m = s.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/);            // 03/09/2015 (format belge)
    if (m) return new Date(+m[3], +m[2] - 1, +m[1]);
    m = normalizeText(s).match(/^(\d{1,2}) ([a-z]+) (\d{4})/);      // 26 décembre 1964 à 01:00…
    if (m && FR_MONTHS[m[2]] !== undefined) return new Date(+m[3], FR_MONTHS[m[2]], +m[1]);
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
};

export const birthKey = (v) => {
  const d = parseBirthDate(v);
  return d ? dayKey(d) : null;
};

/** Date de naissance d'une fiche, quel que soit le nom du champ */
export const recordBirth = (data) =>
  birthKey(data?.dateOfBirth || data?.birthDate || data?.birthday || data?.dateOfExth);

/** Clé de nom : mots triés, donc « Dupont Léa » = « Léa Dupont » = « lea  DUPONT » */
export const personNameKey = (firstName, lastName) => {
  const f = normalizeText(firstName);
  const l = normalizeText(lastName);
  if (!f || l.length < 2) return null;
  return `${f} ${l}`.split(' ').filter(Boolean).sort().join(' ');
};

const birthCompatible = (a, b) => !a || !b || a === b;

/**
 * Regroupe des fiches qui désignent la même personne.
 * @param records [{ id, firstName, lastName, birth, links?: string[], mergedInto?, separate? }]
 * @returns tableau de groupes (tableaux de records)
 */
export function groupPeople(records) {
  const parent = new Map(records.map((r) => [r.id, r.id]));
  const find = (x) => {
    let r = x;
    while (parent.get(r) !== r) r = parent.get(r);
    let c = x;
    while (parent.get(c) !== r) { const n = parent.get(c); parent.set(c, r); c = n; }
    return r;
  };
  const union = (a, b) => {
    if (!parent.has(a) || !parent.has(b)) return;
    const ra = find(a); const rb = find(b);
    if (ra !== rb) parent.set(rb, ra);
  };

  // 1. Liens explicites
  records.forEach((r) => {
    if (r.mergedInto) union(r.mergedInto, r.id);
    (r.links || []).forEach((l) => { if (l && l !== r.id) union(r.id, l); });
  });

  // 2. Même nom + dates de naissance compatibles
  const buckets = new Map();
  records.forEach((r) => {
    if (r.separate) return;
    const k = personNameKey(r.firstName, r.lastName);
    if (!k) return;
    if (!buckets.has(k)) buckets.set(k, []);
    buckets.get(k).push(r);
  });
  buckets.forEach((list) => {
    const byBirth = new Map();
    const noBirth = [];
    list.forEach((r) => {
      if (!r.birth) { noBirth.push(r); return; }
      if (!byBirth.has(r.birth)) byBirth.set(r.birth, []);
      byBirth.get(r.birth).push(r);
    });
    byBirth.forEach((g) => g.slice(1).forEach((r) => union(g[0].id, r.id)));
    // Sans date de naissance : on ne rattache que s'il n'y a pas d'ambiguïté
    if (byBirth.size <= 1) {
      const anchor = byBirth.size === 1 ? Array.from(byBirth.values())[0][0] : noBirth[0];
      noBirth.forEach((r) => union(anchor.id, r.id));
    }
  });

  const groups = new Map();
  records.forEach((r) => {
    const root = find(r.id);
    if (!groups.has(root)) groups.set(root, []);
    groups.get(root).push(r);
  });
  return Array.from(groups.values());
}

/**
 * Cherche une fiche existante pour la même personne (avant d'en créer une).
 * Suit mergedInto pour renvoyer la fiche principale.
 */
export function findSamePerson(list, { firstName, lastName, dateOfBirth }) {
  const key = personNameKey(firstName, lastName);
  if (!key) return null;
  const birth = birthKey(dateOfBirth);
  const byId = new Map(list.map((s) => [s.id, s]));
  for (const s of list) {
    if (s.separateIdentity || personNameKey(s.firstName, s.lastName) !== key) continue;
    // Une fiche fusionnée hérite de la date de naissance de sa fiche principale
    const primary = (s.mergedInto && byId.get(s.mergedInto)) || s;
    const knownBirth = recordBirth(primary) || recordBirth(s);
    if (birthCompatible(birth, knownBirth)) return primary;
  }
  return null;
}
